import { useState, useMemo, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import Convert from 'ansi-to-html';
import {
  Terminal, Download, X, ArrowDown, Search, RefreshCw, Package, Loader2,
} from 'lucide-react';
import { useLogStream, type LogEntry } from '../hooks/useLogStream';
import { Button } from '../components/Button';
import { AccountFilter } from '../components/AccountFilter';
import { FilterToggle } from '../components/ListControls';
import { fetchAccounts } from '../api/client';
import type { AccountSummary } from '../api/types';
import { showToast } from '../hooks/useToast';

const ansi = new Convert({
  fg: '#e2e8f0', bg: '#030712',
  colors: {
    0: '#e2e8f0', 1: '#ef4444', 2: '#22c55e', 3: '#eab308',
    4: '#3b82f6', 5: '#a855f7', 6: '#06b6d4', 7: '#94a3b8',
  },
});

const levelColors: Record<string, string> = {
  DEBUG:   'text-gray-400 dark:text-gray-500',
  INFO:    'text-blue-600 dark:text-blue-400',
  SUCCESS: 'text-emerald-600 dark:text-emerald-400',
  WARNING: 'text-amber-600 dark:text-amber-400',
  ERROR:   'text-red-600 dark:text-red-400',
  CRITICAL:'text-red-700 dark:text-red-400 font-bold',
};

const levels = ['DEBUG', 'INFO', 'SUCCESS', 'WARNING', 'ERROR', 'CRITICAL'];

/**
 * 窄屏下**仅对过长的级别**用缩写；短的（INFO / ERROR / DEBUG）保持原样 ——
 * 它们本来就不占地方，缩写反而平添认知成本。
 *
 * 阈值取 7 个字符：DEBUG/INFO/ERROR 是 4~5 字符，SUCCESS/WARNING/CRITICAL
 * 是 7~8 字符，正好卡在「一行放得下 / 放不下」的分界上。
 *
 * 缩写取通用写法而非机械截前三位：WARNING 截前三位会变成 WAR（英文「战争」）。
 * 过滤值仍是全称（`filterLevels` 存的是 `levels` 里的原值），缩写只影响显示。
 */
const levelShort: Record<string, string> = {
  SUCCESS: 'SUC',
  WARNING: 'WARN',
  CRITICAL: 'CRT',
};

export function LogsPage() {
  const [filterLevels, setFilterLevels] = useState<Set<string>>(new Set());
  // 账户筛选可多选：空数组 = 不过滤；含 '' 表示只看面板级；其余为账户名
  const [filterAccounts, setFilterAccounts] = useState<string[]>([]);
  const [accountOptions, setAccountOptions] = useState<AccountSummary[]>([]);
  // 窄屏下筛选区默认折叠（md 起常驻展开，由 CSS 控制）—— 断点与账户总览一致
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilterCount = filterLevels.size + filterAccounts.length;

  // 级别与账户筛选均下推到后端（源头过滤），变化时 hook 自动重连重拉
  const { entries, connected, authRequired, loading, total, hasMore, loadMore, refresh } =
    useLogStream([...filterLevels], filterAccounts);

  // 账户列表用于筛选下拉；取自面板账户而非已加载日志，
  // 否则筛选后列表会随日志内容塌缩、无法切回其他账户
  useEffect(() => {
    fetchAccounts().then(setAccountOptions).catch(() => { /* 拉不到就只留「全部/面板级」 */ });
  }, []);
  const [keyword, setKeyword] = useState('');
  const [atBottom, setAtBottom] = useState(true);
  const [expanded, setExpanded] = useState<Set<number>>(new Set()); // 展开的日志行
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  // Pip install modal
  const [pipOpen, setPipOpen] = useState(false);
  const [pipPkg, setPipPkg] = useState('');
  const [pipInstalling, setPipInstalling] = useState(false);

  // wrap loadMore to deduplicate calls
  const loadingMore = useRef(false);
  // 锚定滚动位置：加载更早日志时 firstItemIndex 递减，视图不跳动
  const [firstItemIndex, setFirstItemIndex] = useState(0);
  const prevFilteredLength = useRef(0);
  const loadingAnchor = useRef(false); // 标记正在 loadMore，避免 WS 新日志误触锚定

  // Filter
  const toggleLevel = (lv: string) => {
    setFilterLevels((prev) => {
      const next = new Set(prev);
      next.has(lv) ? next.delete(lv) : next.add(lv);
      return next;
    });
  };

  // 级别过滤已由后端完成，这里仅做关键词本地过滤
  const filtered = useMemo(() => {
    if (!keyword) return entries;
    const kw = keyword.toLowerCase();
    return entries.filter((e) => e.message.toLowerCase().includes(kw));
  }, [entries, keyword]);

  const toggleExpand = (seq: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(seq) ? next.delete(seq) : next.add(seq);
      return next;
    });
  };

  // filtered 增长时锚定视图（仅 loadMore 前置插入时）。
  // 使用 useLayoutEffect：绘制前同步调整锚点，避免「先下跳再回位」的中间帧
  useLayoutEffect(() => {
    const prev = prevFilteredLength.current;
    const curr = filtered.length;
    if (curr > prev && loadingAnchor.current) {
      setFirstItemIndex((idx) => idx - (curr - prev));
      loadingAnchor.current = false;
    }
    prevFilteredLength.current = curr;
  }, [filtered.length]);

  const handleLoadMore = useCallback(async () => {
    if (loadingMore.current || !hasMore) return;
    loadingMore.current = true;
    loadingAnchor.current = true; // 标记本次增长来自 loadMore
    await loadMore();
    loadingMore.current = false;
  }, [hasMore, loadMore]);

  // Auto-follow
  useEffect(() => {
    if (atBottom && filtered.length > 0) {
      virtuosoRef.current?.scrollToIndex({ index: filtered.length - 1, behavior: 'smooth' });
    }
  }, [filtered.length, atBottom]);

  // 级别筛选变化时回到最新日志位置（列表按新筛选重建）
  useEffect(() => {
    if (filtered.length > 0) {
      virtuosoRef.current?.scrollToIndex({ index: filtered.length - 1, behavior: 'auto' });
      setAtBottom(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterLevels]);

  const handleScrollBottom = () => {
    virtuosoRef.current?.scrollToIndex({ index: filtered.length - 1, behavior: 'smooth' });
    setAtBottom(true);
  };

  // Export
  const handlePipInstall = async () => {
    if (!pipPkg.trim()) return;
    setPipInstalling(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/config/pip-install', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ package: pipPkg.trim() }),
      });
      const data = await res.json();
      if (res.ok) { showToast('success', data.message); setPipPkg(''); setPipOpen(false); }
      else showToast('error', data.detail);
    } catch { showToast('error', '安装失败'); }
    finally { setPipInstalling(false); }
  };

  const handleExport = () => {
    const text = entries
      .map((l) => `[${new Date(l.timestamp * 1000).toISOString()}] [${l.level}] ${l.message}`)
      .join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mis-miss-${new Date().toISOString().slice(0, 19)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /**
   * 三个操作按钮。定义一次、两处渲染：窄屏放在标题行（始终可见，不受筛选
   * 折叠影响），md 起在筛选面板里。
   * 两处的可见性互补（一个 md:hidden、一个在 hidden md:flex 的面板内），
   * 任何宽度下只会出现一份 —— 提成一份定义就是为了避免两份文案/回调各改各的。
   */
  const logActions = (
    <>
      <Button variant="ghost" size="sm" icon={<RefreshCw />}
        tooltip="刷新" aria-label="刷新" onClick={refresh}>
        <span className="hidden md:inline">刷新</span>
      </Button>
      <Button variant="ghost" size="sm" icon={<Package />}
        tooltip="安装包" aria-label="安装包" onClick={() => setPipOpen(true)}>
        <span className="hidden md:inline">安装包</span>
      </Button>
      <Button variant="ghost" size="sm" icon={<Download />}
        tooltip="导出" aria-label="导出" onClick={handleExport}>
        <span className="hidden md:inline">导出</span>
      </Button>
    </>
  );

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] lg:h-[calc(100vh-6rem)] animate-fade-in">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="min-w-0">
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">服务器日志</h1>
          <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400 mt-1">
            <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${connected ? 'bg-emerald-500' : 'bg-red-500'}`} />
            {connected ? '实时' : authRequired ? '未授权（实时推送已停止）' : '离线'} · 已加载 {entries.length} / 共 {total} 条
            {filtered.length !== entries.length && `（筛选后 ${filtered.length} 条）`}
          </p>
        </div>
        {/* 窄屏：操作按钮不随筛选折叠，始终可见 */}
        <div className="flex items-center gap-1 shrink-0 md:hidden">{logActions}</div>
      </div>

      {/* Search bar：窄屏与「筛选」开关同一行 */}
      <div className="flex items-center gap-2 mb-2">
        <Search className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
        <input type="text" value={keyword} onChange={(e) => setKeyword(e.target.value)}
          placeholder="搜索日志关键词..."
          className="flex-1 min-w-0 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700
                     rounded-md px-3 py-1.5 text-xs
                     text-gray-900 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-600
                     focus:outline-none focus:border-gray-400 dark:focus:border-gray-500 transition-colors" />
        {keyword && (
          <button onClick={() => setKeyword('')}
            className="p-2 -m-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 dark:text-gray-500 shrink-0">
            <X className="w-3 h-3" />
          </button>
        )}
        {/* 与账户总览共用同一组件，窄屏观感一致（此前这里是描边式、断点 sm） */}
        <FilterToggle
          open={filtersOpen}
          count={activeFilterCount}
          onToggle={() => setFiltersOpen((v) => !v)}
        />
      </div>

      {/* 筛选面板：紧跟在「筛选」开关**下方**展开。
          此前它挂在标题行里，而开关在搜索行 —— 展开时面板出现在按钮上方，
          点按钮却在上面出内容，很反直觉。 */}
      <div className={`${filtersOpen ? 'flex' : 'hidden'} md:flex
                       flex-wrap items-center gap-1 mb-2 w-full`}>
        {/* 级别药丸：容器样式与账户总览的筛选胶囊组对齐（浅底圆角 + 内边距）。
            一律 flex-wrap 换行，**不横向滚动** —— 6 个级别加「清除」在窄屏放不下，
            横向滚动没有可滚动的视觉提示，用户只会看到「显示不全」；
            换行虽然占两行，但每个选项都看得见、点得到。 */}
        <div className="flex flex-wrap items-center gap-1
                        max-w-full shrink p-1 rounded-lg bg-gray-100 dark:bg-gray-800">
          {levels.map((lv) => {
            const active = filterLevels.has(lv);
            return (
              <button key={lv} onClick={() => toggleLevel(lv)}
                className={`px-2 py-1 text-xs font-medium rounded-md transition-colors shrink-0
                  ${active
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>
                {/* 只有过长的级别才双份渲染（窄屏缩写 / md 起全称），由 CSS 决定
                    显示哪份 —— 比用 matchMedia 监听宽度简单，也没有首帧闪烁。
                    短的那些直接输出，不多塞一个节点。 */}
                {levelShort[lv] ? (
                  <>
                    <span className="md:hidden">{levelShort[lv]}</span>
                    <span className="hidden md:inline">{lv}</span>
                  </>
                ) : (
                  lv
                )}
              </button>
            );
          })}
          {filterLevels.size > 0 && (
            <button onClick={() => setFilterLevels(new Set())}
              className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-red-500
                         transition-colors shrink-0">清除</button>
          )}
        </div>
        <span className="w-px h-5 bg-gray-300 dark:bg-gray-700 mx-1" />
        <AccountFilter
          accounts={accountOptions}
          selected={filterAccounts}
          onChange={setFilterAccounts}
        />
        <span className="w-px h-5 bg-gray-300 dark:bg-gray-700 mx-1" />
        {/* 次要操作：窄屏那份在标题行且常驻（md:hidden），这份只在 md 起显示 */}
        <div className="hidden md:flex items-center gap-1">{logActions}</div>
      </div>

      {/* Log area */}
      <div className="flex-1 bg-gray-100 dark:bg-gray-950 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden relative">
        {filtered.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-600">
            <div className="text-center">
              {loading ? (
                <><Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin opacity-40" /><p>加载中...</p></>
              ) : (
                <><Terminal className="w-8 h-8 mx-auto mb-2 opacity-30" /><p>等待日志输出...</p></>
              )}
            </div>
          </div>
        ) : (
          <Virtuoso
            ref={virtuosoRef}
            data={filtered}
            firstItemIndex={firstItemIndex}
            followOutput="smooth"
            atBottomStateChange={setAtBottom}
            initialTopMostItemIndex={filtered.length - 1}
            startReached={handleLoadMore}
            itemContent={(_index, entry) => {
              const isExpanded = expanded.has(entry.seq_id);
              // 整行都可点：手机上只有正文那一小条能点的话太难点中。
              // active: 是给触摸端的反馈 —— 手机没有 hover，不加就没有任何按下感
              return (
                <div
                  onClick={() => toggleExpand(entry.seq_id)}
                  title={isExpanded ? '点击收起' : '点击展开'}
                  className={`flex gap-2 leading-5 cursor-pointer active:bg-black/[0.06]
                              dark:active:bg-white/[0.06]
                              ${isExpanded ? 'min-h-5 h-auto' : 'h-5'}
                              hover:bg-black/[0.03] dark:hover:bg-white/[0.03] px-2 text-[11px] font-mono`}>
                  <span className="text-gray-400 dark:text-gray-600 shrink-0 w-16 text-right">
                    {new Date(entry.timestamp * 1000).toLocaleTimeString('zh-CN', { hour12: false })}
                  </span>
                  <span className={`shrink-0 w-16 ${levelColors[entry.level] || 'text-gray-400'}`}>
                    [{entry.level}]
                  </span>
                  {/* 固定行高：默认单行截断，点击展开全文。
                      折叠时右侧留一个展开指示——不然手机用户看不出这行能点 */}
                  <span
                    className={`flex-1 min-w-0 text-gray-700 dark:text-gray-300
                      ${isExpanded ? 'h-auto break-all whitespace-pre-wrap' : 'truncate [&_span]:whitespace-nowrap'}`}
                    dangerouslySetInnerHTML={{ __html: ansi.toHtml(entry.message) }}
                  />
                  <span aria-hidden="true"
                    className="shrink-0 self-center text-gray-300 dark:text-gray-600 text-[9px] leading-none">
                    {isExpanded ? '▲' : '▼'}
                  </span>
                </div>
              );
            }}
          />
        )}

        {/* Scroll-to-bottom FAB */}
        {!atBottom && filtered.length > 20 && (
          <button onClick={handleScrollBottom}
            className="absolute bottom-4 right-4 w-8 h-8 rounded-full bg-white dark:bg-gray-700
                       border border-gray-200 dark:border-gray-600
                       hover:bg-gray-100 dark:hover:bg-gray-600
                       text-gray-600 dark:text-white shadow-lg flex items-center justify-center
                       transition-all animate-slide-in-up">
            <ArrowDown className="w-4 h-4" />
          </button>
        )}
      </div>
      {/* Pip install modal */}
      {pipOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-fade-in" onClick={() => setPipOpen(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 animate-slide-in-up">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">安装 pip 包</h3>
            <p className="text-xs text-gray-500 mb-4">输入包名，将使用 pip 安装到当前 Python 环境</p>
            <div className="flex gap-2">
              <input type="text" value={pipPkg} onChange={e => setPipPkg(e.target.value)}
                className="input flex-1" placeholder="例如: pypinyin"
                onKeyDown={e => e.key === 'Enter' && handlePipInstall()} />
              <Button variant="primary" onClick={handlePipInstall} loading={pipInstalling}>安装</Button>
            </div>
            <div className="flex justify-end mt-3">
              <Button variant="ghost" size="sm" onClick={() => setPipOpen(false)}>取消</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
